import { addMinutes, format } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";

import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import BackIcon from "@mui/icons-material/ArrowBackIosNew";
import NextIcon from "@mui/icons-material/ArrowForwardIos";
import PreviousIcon from "@mui/icons-material/ArrowBackIos";

import { useIsMobile } from "../../../../hooks/useIsMobile";
import GuidanceChart from "./components/chart";
import GuidanceTable from "./components/table";
import GuidanceService from "../../lib/guidance.service";
import IGuidance from "../../types/IGuidance";
import { useNitroStore } from "../../../../store/Store";
import LoadingIcon from "../../../../components/Loading";
import cookie from "../../../../store/cookie";
import useEscapeKeyExit from "../../../../hooks/useEscapeKeyExit";

const dt = new Date();
const today = format(addMinutes(dt, dt.getTimezoneOffset()), "yyyy-MM-dd");

const GuidanceHistory = ({
  location,
  product,
  date,
  close,
}: {
  location: string;
  product: string;
  date: string;
  close: () => void;
}) => {
  useEscapeKeyExit({ close, active: true });
  const auth = useAuth();

  const [savedNotification, failedNotification] = useNitroStore((state) => [
    state.savedNotification,
    state.failedNotification,
  ]);

  const [isLoading, setIsLoading] = useState(true);
  const [records, setRecords] = useState<IGuidance[]>([]);
  const [filteredRecords, setFilteredRecords] = useState<IGuidance[]>([]);
  const [page, setPage] = useState(0);
  const [maxPage, setMaxPage] = useState(0);
  const [pageSize, setPageSize] = useState(6);

  const loadRecords = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await GuidanceService.getIndicationsWithinMonth(
        location,
        product,
        date
      );
      const currentGuidance =
        result.length === 0
          ? {
              date: today,
              fobPrice: 0,
              altPrice: 0,
            }
          : { ...result[result.length - 1] };
      currentGuidance.canEdit = true;

      const newRecords = [...result, currentGuidance];

      setRecords(newRecords);
      setPage(0);
      setMaxPage(Math.ceil(newRecords.length / pageSize) - 1);
    } catch (err) {
      failedNotification("Uhoh, something went wrong.", (err as any).message);
      console.error(err);
      setRecords([]);
    }
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, product, date, failedNotification]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords, location, product, date]);

  useEffect(() => {
    if (page === 0) {
      setFilteredRecords(records.slice(pageSize * -1));
    } else {
      setFilteredRecords(
        records.slice((page + 1) * (pageSize * -1), page * (pageSize * -1))
      );
    }
  }, [page, records, pageSize]);

  const isMobile = useIsMobile(768);
  const isTablet = useIsMobile(1334);
  useEffect(() => {
    if (isTablet) {
      setPageSize(3);
      setPage(0);
      setMaxPage(Math.ceil(records.length / 3) - 1);
    } else {
      setPageSize(6);
      setPage(0);
      setMaxPage(Math.ceil(records.length / 6) - 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTablet]);

  const [editing, setEditing] = useState(false);

  const save = async () => {
    setIsLoading(true);

    try {
      const userName = `${auth.user?.profile.given_name} ${auth.user?.profile.family_name}`;

      const params = {
        records: [records[records.length - 1]],
        userName,
        currency: cookie.currency,
        uom: cookie.uom,
        product,
        location,
      };

      params.records[0].date = date;
      await GuidanceService.createGuidance(params);

      setIsLoading(false);
      loadRecords();
      setEditing(false);
      savedNotification("Guidance saved successfully.");
    } catch (err) {
      setIsLoading(false);
      failedNotification("Uhoh, something went wrong.", (err as any).message);
      console.error(err);
    }
  };

  const [isDirty, setIsDirty] = useState(false);
  useEffect(() => {
    // if last record is different from second to last record

    if (records.length > 1) {
      const last = records[records.length - 1];
      const secondToLast = records[records.length - 2];

      if (
        last.fobPrice !== secondToLast.fobPrice ||
        last.altPrice !== secondToLast.altPrice
      ) {
        setIsDirty(true);
      } else {
        setIsDirty(false);
      }
    }
  }, [records]);

  return (
    <div
      style={{
        flexGrow: 1,
        overflow: "auto",
        height: "50vh",
        borderRadius: isTablet ? 0 : 20,
        width: isMobile ? "100%" : 900,
        maxWidth: isMobile ? "100%" : "calc(100vw - 40px)",
        zIndex: editing ? 101 : undefined,
        backgroundColor: "#F7F7F7",
        boxShadow:
          "0px 4px 4px 0px rgba(0, 0, 0, 0.25), 0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
      }}
    >
      {editing && (
        <div
          style={{
            borderRadius: isTablet ? 0 : 20,
            zIndex: 22,
            position: "absolute",
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: "rgba(15, 44, 82, 0.5)",
          }}
        />
      )}

      {/* HEADER */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          height: 70,
          minHeight: 70,
          borderBottom: "1px solid #D9D9D9",
        }}
      >
        <IconButton
          style={{
            borderRadius: 2,
            borderTopLeftRadius: 18,
            padding: "0px 14px",
            height: "100%",
          }}
          onClick={close}
        >
          <BackIcon className="GuidanceHeaderButton" />
        </IconButton>

        <div className="GuidanceHeader">
          {`Guidance / ${format(new Date(`${date}T00:00`), "MMMM")} History`}
        </div>
      </div>
      {/* END HEADER */}

      {/* <div
        style={{
          height: 100,
          display: "flex",
          alignItems: "center",
          gap: 4,
          paddingLeft: 12,
          paddingRight: 20,
          backgroundColor: "#F2F8FD",
        }}
      >
        <IconButton
          style={{
            padding: 0,
            height: 44,
            width: 44,
            borderRadius: 6,
          }}
          onClick={close}
        >
          <BackIcon style={{ fontSize: 34 }} />
        </IconButton>
        <div
          style={{
            color: "#0F2C52",
            fontSize: isMobile ? 24 : 28,
            fontWeight: 700,
          }}
        >{`Guidance / ${format(
          new Date(`${date}T00:00`),
          "MMMM"
        )} History`}</div>
      </div> */}

      <div
        id="guidance-history-body"
        style={{
          overflowX: "auto",
          overflowY: "auto",
          paddingTop: 20,
          paddingBottom: 20,
        }}
      >
        {isLoading && (
          <div
            style={{
              height: 300,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <LoadingIcon color="#005CA6" height={100} width={100} />
          </div>
        )}

        {!isLoading && records.length === 0 && (
          <div
            style={{
              height: 300,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontStyle: "italic",
              color: "#595959",
            }}
          >
            No guidance history
          </div>
        )}

        {!isLoading && records.length > 0 && (
          <div
            style={{
              padding: "0px 20px",
              display: "flex",
              flexDirection: "column",
              gap: 20,
            }}
          >
            <GuidanceChart data={filteredRecords} />

            <div style={{ zIndex: editing ? 25 : undefined }}>
              <GuidanceTable
                data={filteredRecords}
                editing={editing}
                setEditing={setEditing}
                updateCurrent={(current: IGuidance) => {
                  const newRecords = [...records];
                  newRecords.pop();
                  newRecords.push(current);
                  setRecords(newRecords);
                }}
              />

              {!editing && (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <IconButton
                    disabled={page >= maxPage}
                    onClick={() => {
                      setPage(page + 1);
                    }}
                  >
                    <PreviousIcon />
                  </IconButton>

                  <IconButton
                    disabled={page === 0}
                    onClick={() => {
                      if (page > 0) {
                        setPage(page - 1);
                      }
                    }}
                  >
                    <NextIcon />
                  </IconButton>
                </div>
              )}

              {editing && (
                <div
                  style={{
                    padding: 10,
                    backgroundColor: "white",
                    zIndex: editing ? 30 : undefined,
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 15 }}
                  >
                    <Button
                      id="guidanceHistoryCancelButton"
                      color="error"
                      variant="contained"
                      onClick={() => {
                        setEditing(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      id="guidanceHistorySaveButton"
                      variant="contained"
                      onClick={save}
                      disabled={!isDirty}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GuidanceHistory;
